import { useState, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { uploadContent, uploadContentFile, type UploadResult } from '../api'

type ContentType = 'html' | 'pdf' | 'ppt'
type AccessType = 'logged_in' | 'phone_list' | 'admin_only'
type ExpiryOption = 'never' | '7d' | '30d' | 'custom'

function expiryToIso(opt: ExpiryOption, custom: string): string | null {
  if (opt === 'never') return null
  if (opt === '7d') return new Date(Date.now() + 7 * 86400_000).toISOString()
  if (opt === '30d') return new Date(Date.now() + 30 * 86400_000).toISOString()
  if (opt === 'custom' && custom) return new Date(custom).toISOString()
  return null
}

export default function Upload() {
  const navigate = useNavigate()
  void navigate // used in success screen via Link; kept for future programmatic nav

  const [title, setTitle] = useState('')
  const [type, setType] = useState<ContentType>('html')
  const [htmlText, setHtmlText] = useState('')
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [pptText, setPptText] = useState('')
  const [tags, setTags] = useState('')
  const [description, setDescription] = useState('')
  const [accessType, setAccessType] = useState<AccessType>('logged_in')
  const [phoneList, setPhoneList] = useState('')
  const [expiry, setExpiry] = useState<ExpiryOption>('never')
  const [customExpiry, setCustomExpiry] = useState('')

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<UploadResult | null>(null)
  const [copied, setCopied] = useState(false)

  const fileRef = useRef<HTMLInputElement>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (!title.trim()) { setError('请填写标题'); return }

    const parsedTags = tags.split(',').map((t) => t.trim()).filter(Boolean)
    const parsedPhones = phoneList.split(',').map((p) => p.trim()).filter(Boolean)
    const expiresAt = expiryToIso(expiry, customExpiry)

    const permission = {
      access_type: accessType,
      phone_list: parsedPhones,
      expires_at: expiresAt,
    }

    setLoading(true)
    try {
      let res: UploadResult

      if (type === 'pdf' && pdfFile) {
        // Use multipart for actual file upload
        const fd = new FormData()
        fd.append('title', title)
        fd.append('type', 'pdf')
        fd.append('file', pdfFile)
        fd.append('tags', parsedTags.join(','))
        fd.append('description', description)
        fd.append('permission_type', accessType)
        fd.append('phone_list', parsedPhones.join(','))
        if (expiresAt) fd.append('expires_at', expiresAt)
        res = await uploadContentFile(fd)
      } else {
        // JSON upload for HTML / PPT text content
        const content = type === 'html' ? htmlText : pptText
        if (!content.trim()) { setError('请填写内容'); setLoading(false); return }
        res = await uploadContent({ title, type, content, tags: parsedTags, description, permission })
      }

      setResult(res)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '上传失败，请重试')
    } finally {
      setLoading(false)
    }
  }

  function copyLink() {
    if (!result) return
    navigator.clipboard.writeText(result.url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  // Success screen
  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-900/50 border border-green-700">
            <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold text-white mb-1">上传成功</h2>
            <p className="text-gray-400 text-sm">「{result.title}」已发布</p>
          </div>
          <div className="card">
            <p className="text-xs text-gray-500 mb-2">访问链接</p>
            <div className="flex gap-2">
              <input
                readOnly
                value={result.url}
                className="input text-sm flex-1 font-mono"
              />
              <button onClick={copyLink} className="btn-primary px-3 shrink-0">
                {copied ? '已复制' : '复制'}
              </button>
            </div>
          </div>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => {
                setResult(null); setTitle(''); setHtmlText(''); setPdfFile(null)
                setPptText(''); setTags(''); setDescription('')
              }}
              className="btn-secondary"
            >
              继续上传
            </button>
            <Link to="/admin" className="btn-primary">返回列表</Link>
            <a href={result.url} target="_blank" rel="noreferrer" className="btn-secondary">
              预览
            </a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-950 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/admin" className="text-gray-400 hover:text-gray-200 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 className="font-semibold text-white">上传新内容</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic info */}
          <div className="card space-y-4">
            <h2 className="font-medium text-gray-300 text-sm uppercase tracking-wider">基本信息</h2>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">标题 *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="输入内容标题"
                className="input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">类型 *</label>
              <div className="flex gap-2">
                {(['html', 'pdf', 'ppt'] as ContentType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      type === t
                        ? 'bg-blue-600 border-blue-500 text-white'
                        : 'bg-gray-900 border-gray-700 text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {t.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">描述</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="可选的内容描述"
                rows={2}
                className="input resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">标签</label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="用逗号分隔，例如：AI,教程,2024"
                className="input"
              />
            </div>
          </div>

          {/* Content input */}
          <div className="card space-y-4">
            <h2 className="font-medium text-gray-300 text-sm uppercase tracking-wider">内容</h2>

            {type === 'html' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">HTML 内容 *</label>
                <textarea
                  value={htmlText}
                  onChange={(e) => setHtmlText(e.target.value)}
                  placeholder="粘贴 HTML 代码..."
                  rows={12}
                  className="input resize-y font-mono text-xs"
                  required
                />
              </div>
            )}

            {type === 'pdf' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">PDF 文件 *</label>
                <div
                  onClick={() => fileRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    pdfFile
                      ? 'border-blue-600 bg-blue-900/10'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                >
                  {pdfFile ? (
                    <div>
                      <p className="text-green-400 font-medium">{pdfFile.name}</p>
                      <p className="text-gray-500 text-sm mt-1">
                        {(pdfFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  ) : (
                    <div className="text-gray-500">
                      <svg className="w-10 h-10 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p>点击选择 PDF 文件</p>
                      <p className="text-xs mt-1">最大 50MB</p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="hidden"
                  onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
                />
              </div>
            )}

            {type === 'ppt' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">PPT 内容 (base64 或文本) *</label>
                <textarea
                  value={pptText}
                  onChange={(e) => setPptText(e.target.value)}
                  placeholder="粘贴 PPTX base64 编码内容..."
                  rows={8}
                  className="input resize-y font-mono text-xs"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  提示：使用 base64 编码上传 PPTX 文件内容
                </p>
              </div>
            )}
          </div>

          {/* Permission settings */}
          <div className="card space-y-4">
            <h2 className="font-medium text-gray-300 text-sm uppercase tracking-wider">访问权限</h2>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">谁可以访问</label>
              <div className="space-y-2">
                {[
                  { value: 'logged_in', label: '所有已登录用户', desc: '任何通过验证的用户都可访问' },
                  { value: 'phone_list', label: '指定手机号', desc: '仅允许列表中的手机号访问' },
                  { value: 'admin_only', label: '仅管理员', desc: '只有管理员账号可访问' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      accessType === opt.value
                        ? 'border-blue-600 bg-blue-900/10'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="access_type"
                      value={opt.value}
                      checked={accessType === opt.value}
                      onChange={() => setAccessType(opt.value as AccessType)}
                      className="mt-0.5 accent-blue-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-200">{opt.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {accessType === 'phone_list' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">手机号列表</label>
                <textarea
                  value={phoneList}
                  onChange={(e) => setPhoneList(e.target.value)}
                  placeholder="用逗号分隔，例如：13800138000,13900139000"
                  rows={3}
                  className="input resize-none"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">有效期</label>
              <div className="flex gap-2 flex-wrap">
                {([
                  { value: 'never', label: '永不过期' },
                  { value: '7d', label: '7 天' },
                  { value: '30d', label: '30 天' },
                  { value: 'custom', label: '自定义' },
                ] as { value: ExpiryOption; label: string }[]).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setExpiry(opt.value)}
                    className={`px-4 py-1.5 rounded-lg text-sm border transition-colors ${
                      expiry === opt.value
                        ? 'bg-blue-600 border-blue-500 text-white'
                        : 'bg-gray-900 border-gray-700 text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {expiry === 'custom' && (
                <input
                  type="datetime-local"
                  value={customExpiry}
                  onChange={(e) => setCustomExpiry(e.target.value)}
                  className="input mt-3 max-w-xs"
                />
              )}
            </div>
          </div>

          {error && (
            <div className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <Link to="/admin" className="btn-secondary flex-1 justify-center">取消</Link>
            <button type="submit" className="btn-primary flex-1" disabled={loading}>
              {loading ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : null}
              {loading ? '上传中...' : '发布内容'}
            </button>
          </div>
        </form>
      </main>
    </div>
  )
}
